import { useSearchBrowseData } from '@aibees/elephant/context/search-browse-data-provider';
import { useEntityType } from '@aibees/elephant/hooks';
import { useIsPeopleLookalikeEnabled } from '@aibees/elephant/hooks/people';
import { LookalikeButtonBase } from '@aibees/shared/components';
import { useAdvanceTableConfigContext } from '@aibees/shared/features/advance-table/context';
import { classnames } from '@aibees/shared/utils';
import { Button, ClickAwayListener, Stack, Tooltip } from '@mui/material';
import { memo, useMemo } from 'react';
import { RelevancySummary } from '../relevancy-summary';
import { LookalikeButtonIcon } from './components/lookalike-button-icon';
import { LookalikeButtonSidebar } from './components/lookalike-button-sidebar';
import { LookalikeButtonTooltipContent } from './components/lookalike-button-tooltip-content';
import { useLookalikeHandlerWithTop } from './hooks/use-lookalike-handler';
import { useSelectedLookalikeKey } from './hooks/use-selected-lookalike-key';
import { LookalikeButton } from './lookalike-button';
import * as Styled from './styles';
import * as SX from './sx';
import type { LookalikeButtonProps as Props } from './types';

/**
 * Table-row variant that reads defaultValues and top from context and uses
 * useLookalikeHandlerWithTop so it does not call useBrowseCompanySearchCore
 * or useBrowsePeopleSearchCore. Use in company/people table configs.
 * Falls back to LookalikeButton when outside the browse-data provider.
 */
export const LookalikeButtonWithSharedData = memo(
  ({
    className,
    currentItem,
    isSidebar,
    hideScore,
    disabled,
    fullWidth,
    showPopover,
  }: Props) => {
    const browseData = useSearchBrowseData();
    const { maxStickyRows } = useAdvanceTableConfigContext();
    const { isPeople } = useEntityType(currentItem);
    const isPeopleLookalikeEnabled = useIsPeopleLookalikeEnabled();
    const isPeopleLookalikeDisabled = useMemo(
      () => !isPeopleLookalikeEnabled && isPeople,
      [isPeopleLookalikeEnabled, isPeople]
    );
    const { lookalikeKey, selected } = useSelectedLookalikeKey(currentItem);
    const top = browseData?.top ?? 0;
    const { handleClick, changeOpen, open } = useLookalikeHandlerWithTop(
      currentItem,
      top
    );

    if (browseData == null) {
      return (
        <LookalikeButton
          className={className}
          currentItem={currentItem}
          isSidebar={isSidebar}
          hideScore={hideScore}
          disabled={disabled}
          fullWidth={fullWidth}
          showPopover={showPopover}
        />
      );
    }

    const { defaultValues } = browseData;
    const isButtonDisabled =
      !currentItem.canSimilaritySearch || isPeopleLookalikeDisabled;
    const showLookalikeLabel = selected || !currentItem.relevancy;
    const items = defaultValues;

    if (!lookalikeKey) return null;

    return (
      <Styled.Wrapper
        className={classnames('LookalikeButton', className)}
        sx={SX.root}
        key={currentItem.id}
      >
        {isSidebar || showPopover ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <LookalikeButtonSidebar
              currentItem={currentItem}
              lookalikeKey={lookalikeKey}
              maxStickyRows={maxStickyRows ?? 0}
              onAddToCurrentSearch={handleClick}
              selected={selected}
              hideScore={hideScore}
              disabled={isButtonDisabled || disabled}
            />
          </Stack>
        ) : (
          <>
            {!selected && !!currentItem.relevancy && (
              <RelevancySummary relevancy={currentItem.relevancy} />
            )}

            <ClickAwayListener onClickAway={() => changeOpen(false)}>
              <Tooltip
                title={
                  <LookalikeButtonTooltipContent
                    max={maxStickyRows ?? 0}
                    onClose={() => changeOpen(false)}
                  />
                }
                arrow
                placement="top"
                open={open.value}
                onClose={() => changeOpen(false)}
              >
                {showLookalikeLabel ? (
                  <Button
                    className={classnames({
                      selected: showLookalikeLabel,
                    })}
                    startIcon={<LookalikeButtonIcon selected={selected} />}
                    disabled={isButtonDisabled}
                    color={selected ? 'primary' : 'inherit'}
                    variant="outlined"
                    onClick={handleClick}
                    size="small"
                    fullWidth={fullWidth}
                  >
                    Lookalike
                  </Button>
                ) : (
                  <LookalikeButtonBase
                    limitReached={items.length === maxStickyRows}
                    errorMessage="You have reached the limit of 5 lookalikes, Remove to add new ones."
                    type="icon"
                    onClick={handleClick}
                  />
                )}
              </Tooltip>
            </ClickAwayListener>
          </>
        )}
      </Styled.Wrapper>
    );
  }
);

LookalikeButtonWithSharedData.displayName = 'LookalikeButtonWithSharedData';
